"use client";

import { useNoteLabel } from "@/hooks/use-notation";
import {
  MAJOR_TONALITIES,
  MINOR_TONALITIES,
  tonalityEquals,
  type Tonality,
} from "@/lib/music/tonality";
import { FONT_MONO, signatureBadge } from "./tonality-picker-helpers";

type Props = {
  tonality: Tonality | null;
  onPick: (t: Tonality) => void;
  onClear: () => void;
};

export function TonalityPickerFreePanel({ tonality, onPick, onClear }: Props) {
  return (
    <div>
      <div className="mb-3 flex items-baseline justify-between">
        <span
          className="text-[10px] font-semibold uppercase tracking-[0.12em]"
          style={{ color: "var(--color-text-muted)", fontFamily: FONT_MONO }}
        >
          Tonalité
        </span>
        {tonality && (
          <button
            type="button"
            onClick={onClear}
            className="text-[11px] cursor-pointer"
            style={{ color: "var(--color-text-secondary)" }}
          >
            Effacer
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3.5">
        <TonalityColumn
          title="Majeur"
          options={MAJOR_TONALITIES}
          active={tonality?.mode === "major" ? tonality : null}
          onPick={onPick}
        />
        <TonalityColumn
          title="Mineur"
          options={MINOR_TONALITIES}
          active={tonality?.mode === "minor" ? tonality : null}
          onPick={onPick}
        />
      </div>

      <p
        className="mt-3 border-t pt-3 text-[10px] italic"
        style={{
          borderColor: "var(--color-border-subtle)",
          color: "var(--color-text-muted)",
        }}
      >
        Charger un morceau remplace ce sélecteur par un contrôle de
        transposition.
      </p>
    </div>
  );
}

type TonalityColumnProps = {
  title: string;
  options: Tonality[];
  active: Tonality | null;
  onPick: (t: Tonality) => void;
};

function TonalityColumn({
  title,
  options,
  active,
  onPick,
}: TonalityColumnProps) {
  const toNoteLabel = useNoteLabel();

  const suffix = title === "Majeur" ? "maj" : "m";

  return (
    <div>
      <div
        className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em]"
        style={{ color: "var(--color-text-secondary)" }}
      >
        {title}
      </div>
      <div className="flex flex-col gap-px">
        {options.map((t) => {
          const isActive = tonalityEquals(t, active);
          const sig = signatureBadge(t);

          return (
            <button
              key={`${t.mode}-${t.tonic}`}
              type="button"
              onClick={() => onPick(t)}
              aria-pressed={isActive}
              className={`flex items-center justify-between rounded px-2 py-[5px] text-[12px] tabular-nums cursor-pointer transition-colors ${
                isActive ? "" : "hover:bg-white/[0.04]"
              }`}
              style={{
                ...(isActive && {
                  background: "var(--color-accent-subtle)",
                }),
                color: isActive
                  ? "var(--color-text-primary)"
                  : "var(--color-text-secondary)",
                border: `1px solid ${
                  isActive ? "var(--color-glass-border)" : "transparent"
                }`,
                fontFamily: FONT_MONO,
                fontWeight: 500,
              }}
            >
              <span>
                {toNoteLabel(t.tonic)} {suffix}
              </span>
              <span
                className="text-[10px]"
                style={{ color: "var(--color-text-muted)" }}
              >
                {sig ?? ", "}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
